/**
 * /market/:slug/promo/:promoId — Landing dedicada por oferta (LDP).
 * Carga el catálogo server-side y muestra un header contextual de la oferta
 * sobre el PLP filtrado por promoId.
 */
import { useState, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { Tag } from "lucide-react";
import { loadMarketCatalog, type MarketCatalogResult } from "@/lib/market-catalog";
import { renderMarketPublicOffline } from "@/lib/market-public-offline-gate";
import { MarketPlpPage } from "@/components/market/MarketPlpPage";
import { MarketPlpSkeleton } from "@/components/market/MarketSkeletons";

const FONT_LINK = {
  rel: "stylesheet",
  href: "https://fonts.googleapis.com/css2?family=Hanken+Grotesk:wght@400;600;700;800&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@600&display=swap",
};

const fetchCatalog = createServerFn({ method: "GET" })
  .inputValidator((slug: string) => slug)
  // @ts-expect-error ServerFnCtx framework type mismatch
  .handler(async ({ data: slug }): Promise<MarketCatalogResult> => {
    return loadMarketCatalog(slug, { request: getRequest() });
  });

export const Route = createFileRoute("/market_/$slug/promo/$promoId")({
  pendingComponent: MarketPlpSkeleton,
  pendingMs: 0,
  loader: async ({ params }) => fetchCatalog({ data: params.slug }),
  component: PromoLdpRoute,
  head: ({ params, loaderData }) => {
    const manifestLink = { rel: "manifest", href: `/api/manifest/${params.slug}` };
    const result = loaderData as MarketCatalogResult | undefined;
    const name = result?.ok ? result.data.tenant.name : params.slug;
    return {
      meta: [
        { title: `Oferta especial — ${name}` },
        { name: "description", content: `Aprovechá esta oferta especial en ${name}` },
      ],
      links: [manifestLink, FONT_LINK],
    };
  },
});

type OfferSummary = {
  id: string;
  title: string;
  body: string | null;
  badge: string;
  imageUrl: string | null;
};

function useOffer(slug: string, promoId: string): OfferSummary | null {
  const [offer, setOffer] = useState<OfferSummary | null>(null);
  useEffect(() => {
    fetch(`/api/market/${encodeURIComponent(slug)}/promotions`)
      .then((r) => r.json())
      .then((data: { offers?: OfferSummary[] }) => {
        const found = (data.offers ?? []).find((o) => o.id === promoId);
        if (found) setOffer(found);
      })
      .catch(() => {});
  }, [slug, promoId]);
  return offer;
}

function PromoLdpRoute() {
  const { slug, promoId } = Route.useParams();
  const result = Route.useLoaderData() as MarketCatalogResult;
  const offer = useOffer(slug, promoId);

  const offline = renderMarketPublicOffline(result);
  if (offline) return offline;

  if (!result.ok) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6 text-center">
        <p className="text-muted-foreground">Tienda no disponible.</p>
      </div>
    );
  }

  return (
    <>
      {offer && (
        <div className="border-b bg-muted/40 px-4 py-4 sm:px-6">
          <div className="mx-auto flex max-w-screen-xl items-center gap-4">
            {offer.imageUrl && (
              <img
                src={offer.imageUrl}
                alt={offer.title}
                className="h-16 w-16 flex-none rounded-lg object-cover"
              />
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Tag className="h-4 w-4 flex-none text-primary" />
                <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
                  {offer.badge}
                </span>
              </div>
              <h1 className="mt-1 truncate text-lg font-bold leading-tight">{offer.title}</h1>
              {offer.body && (
                <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">{offer.body}</p>
              )}
            </div>
          </div>
        </div>
      )}
      <MarketPlpPage
        slug={slug}
        data={result.data}
        initialPromoId={promoId}
      />
    </>
  );
}
